// Combos.tsx
//
// O que a Central sabe de combo é UMA MARCA: `productCombo: true` dentro do
// `organizationInfoValue`, ao lado de `productServiceTax`. Não há composição,
// não há quantidade, não há regra de troca. Esta tela mostra o que existe e diz,
// sem rodeio, o que não existe — em vez de desenhar um montador de combo que a
// Central não teria onde gravar.
import React, { useMemo } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/cardapioSection/index";
import { Cardapio, formatarReais } from "./regrasDeSaneamento";

interface Props {
  cardapio: Cardapio;
}

const Combos: React.FC<Props> = ({ cardapio }) => {
  const combos = useMemo(
    () => cardapio.produtos.filter((produto) => produto.combo),
    [cardapio]
  );

  const duplicadosDeCanal = combos.filter((combo) =>
    combo.nome.toUpperCase().startsWith("IF ")
  ).length;

  return (
    <>
      <AlfredoStyles.Header>
        <AlfredoStyles.HeaderContent>
          <AlfredoStyles.HeaderTitle>
            <AlfredoStyles.MaterialIcons
              style={{ fontSize: "calc(var(--u) * 36)" }}
            >
              fastfood
            </AlfredoStyles.MaterialIcons>
            Combos
          </AlfredoStyles.HeaderTitle>
          <AlfredoStyles.HeaderSubtitle>
            {combos.length} produto(s) marcados como combo no cadastro da rede
          </AlfredoStyles.HeaderSubtitle>
        </AlfredoStyles.HeaderContent>
      </AlfredoStyles.Header>

      <AlfredoStyles.Cartao>
        <AlfredoStyles.CartaoTitulo>
          <AlfredoStyles.MaterialIcons>check_circle</AlfredoStyles.MaterialIcons>
          O que existe no dado da Central
        </AlfredoStyles.CartaoTitulo>
        <AlfredoStyles.CartaoTexto>
          Um campo booleano por produto: <code>productCombo</code>. Ele diz que
          aquele produto é um combo, e mais nada. O preço continua sendo o da
          categoria, igual a qualquer outro produto.
        </AlfredoStyles.CartaoTexto>

        {combos.length === 0 ? (
          <AlfredoStyles.Aviso $tom="atencao">
            Nenhum produto desta carga veio marcado como combo.
          </AlfredoStyles.Aviso>
        ) : (
          <AlfredoStyles.TabelaEnvolucro>
            <AlfredoStyles.Tabela>
              <thead>
                <tr>
                  <AlfredoStyles.Th>Código</AlfredoStyles.Th>
                  <AlfredoStyles.Th>Combo</AlfredoStyles.Th>
                  <AlfredoStyles.Th>Categoria</AlfredoStyles.Th>
                  <AlfredoStyles.Th $direita>Preço</AlfredoStyles.Th>
                  <AlfredoStyles.Th>Composição</AlfredoStyles.Th>
                </tr>
              </thead>
              <tbody>
                {combos.map((combo) => (
                  <AlfredoStyles.Tr key={combo.uuid || combo.codigo}>
                    <AlfredoStyles.Td $mono>
                      {combo.codigo || "—"}
                    </AlfredoStyles.Td>
                    <AlfredoStyles.Td>{combo.nome}</AlfredoStyles.Td>
                    <AlfredoStyles.Td>
                      {combo.categorias.length > 0
                        ? combo.categorias.join(", ")
                        : "sem categoria"}
                    </AlfredoStyles.Td>
                    <AlfredoStyles.Td $direita $mono>
                      {formatarReais(combo.preco)}
                    </AlfredoStyles.Td>
                    <AlfredoStyles.Td>
                      {/* Não é "vazio": é que o dado não existe. Uma célula em
                          branco aqui seria lida como "combo sem itens". */}
                      <AlfredoStyles.Etiqueta $tom="media">
                        não vem na carga
                      </AlfredoStyles.Etiqueta>
                    </AlfredoStyles.Td>
                  </AlfredoStyles.Tr>
                ))}
              </tbody>
            </AlfredoStyles.Tabela>
          </AlfredoStyles.TabelaEnvolucro>
        )}

        {duplicadosDeCanal > 0 && (
          <AlfredoStyles.CartaoNota>
            {duplicadosDeCanal} desses {combos.length} são cópias com o prefixo
            "IF", criadas para ter outro preço no iFood. São o mesmo combo
            contado duas vezes — ver a sub-tela Preço por canal.
          </AlfredoStyles.CartaoNota>
        )}
      </AlfredoStyles.Cartao>

      <AlfredoStyles.Cartao>
        <AlfredoStyles.CartaoTitulo>
          <AlfredoStyles.MaterialIcons>block</AlfredoStyles.MaterialIcons>
          O que NÃO existe no dado da Central
        </AlfredoStyles.CartaoTitulo>
        <AlfredoStyles.CartaoTexto>
          Nada disso chega na Central hoje, e por isso nada disso é editável
          aqui:
        </AlfredoStyles.CartaoTexto>
        <AlfredoStyles.ListaRegistros>
          <AlfredoStyles.RegistroLinha>
            <AlfredoStyles.RegistroNome>
              A composição do combo
            </AlfredoStyles.RegistroNome>
            <AlfredoStyles.RegistroDetalhe>
              quais produtos entram e em que quantidade. O valor gravado do
              produto tem exatamente dois campos, e nenhum deles é uma lista de
              itens.
            </AlfredoStyles.RegistroDetalhe>
          </AlfredoStyles.RegistroLinha>
          <AlfredoStyles.RegistroLinha>
            <AlfredoStyles.RegistroNome>
              A regra de troca
            </AlfredoStyles.RegistroNome>
            <AlfredoStyles.RegistroDetalhe>
              o que o cliente pode substituir dentro do combo, e o que isso
              soma no preço.
            </AlfredoStyles.RegistroDetalhe>
          </AlfredoStyles.RegistroLinha>
          <AlfredoStyles.RegistroLinha>
            <AlfredoStyles.RegistroNome>
              O desconto do combo
            </AlfredoStyles.RegistroNome>
            <AlfredoStyles.RegistroDetalhe>
              a diferença entre o preço do combo e a soma dos itens. O combo
              aqui é só mais um produto com um preço próprio na categoria; não
              há relação registrada entre esse preço e o dos componentes.
            </AlfredoStyles.RegistroDetalhe>
          </AlfredoStyles.RegistroLinha>
          <AlfredoStyles.RegistroLinha>
            <AlfredoStyles.RegistroNome>
              A vigência do combo
            </AlfredoStyles.RegistroNome>
            <AlfredoStyles.RegistroDetalhe>
              dia e horário em que a promoção vale. Não há campo de período no
              cadastro de produto.
            </AlfredoStyles.RegistroDetalhe>
          </AlfredoStyles.RegistroLinha>
        </AlfredoStyles.ListaRegistros>

        <AlfredoStyles.Aviso $tom="atencao">
          Quem monta combo é o OPS, e é o PDV que sabe executá-lo. Enquanto não
          existir rota de gravação de composição de combo para a Central, esta
          tela continua sendo um relatório — e não vai ganhar um botão de salvar
          que não teria onde gravar.
        </AlfredoStyles.Aviso>
      </AlfredoStyles.Cartao>
    </>
  );
};

export default Combos;
